import { isEevoHost } from '@upright/common/arvo/public-url';
import { isDev } from '@upright/common/environment';

/**
 * Resolve the API server base URL for the current environment.
 *
 * Deliberately kept in its own module rather than in `untidy/urls.ts`, even though every
 * other URL helper lives there. `urls.ts` imports from `views/**`, `api/common/hooks` and
 * the Redux selectors, so anything importing it pulls in a ~312-module closure — and one
 * of those modules reads `localStorage` at module scope, which throws outside a browser.
 * `api/common/fetcher-core.ts` needs only this function, so keeping it here is what lets
 * the fetch path stay a handful of leaf modules and stay importable in Node (Storybook
 * module mocks, tests).
 *
 * Keep this module free of imports beyond `@upright/common/environment` and
 * `@upright/common/arvo/public-url`, which imports nothing.
 *
 * On Eevo's own hosts (eevo.io, PLT-7477) the API is the page's own origin, whatever the build
 * baked in, so the console's requests are same-origin and its session cookies first-party.
 *
 * @returns Base URL of the API server, or `undefined` in development when no override applies
 */
export function getServerBaseUrl() {
  if (typeof window !== 'undefined' && isEevoHost(window.location.hostname)) return window.location.origin;
  if (isDev()) {
    if (process.env.LOCAL_IP) return `http://${process.env.LOCAL_IP}:3001`;
    const host = typeof window !== 'undefined' && window.location.host;
    if (host && host.indexOf('localhost-staging') > -1) return 'https://staging.uprightplatform.com';
    if (host && host.indexOf('localhost-production') > -1) return 'https://uprightplatform.com';
    return process.env.SERVER_BASE_URL;
  }
  if (process.env.SERVER_BASE_URL) return process.env.SERVER_BASE_URL;
  throw Error('SERVER_BASE_URL is not set');
}
